import { useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Нижняя шторка — подтверждение необратимого.
 *
 * Нужна там, где кошелёк подтверждения не заменяет: он покажет сумму и
 * адрес, но не объяснит, что именно человек отдаёт. Передача позиции по
 * введённому адресу — как раз такой случай: опечатка в адресе означает, что
 * право на все оставшиеся платежи уходит незнакомцу без возврата.
 *
 * Не диалог браузера: confirm() блокирует поток и выглядит чужеродно в
 * Mini App, а на iOS в WebView его вид непредсказуем.
 */
export function Sheet({
  title,
  children,
  confirmLabel,
  danger = false,
  busy = false,
  disabled = false,
  closeLabel,
  onConfirm,
  onClose,
}: {
  title: string;
  children?: ReactNode;
  /** Нет подписи — нет и кнопки: шторка бывает и просто рассказом о том,
   *  что сейчас происходит, без действия от человека. */
  confirmLabel?: string | undefined;
  /** Красная кнопка: для того, что нельзя отменить. */
  danger?: boolean;
  busy?: boolean;
  /** Действие заведомо не пройдёт — кнопка есть, но не нажимается. Причину
   *  шторка объясняет сама: кнопка без объяснения хуже её отсутствия. */
  disabled?: boolean;
  /** Своя надпись на «закрыть»: «позже» и «закрыть» — разные ответы. */
  closeLabel?: string | undefined;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  // Escape закрывает, фон под шторкой не прокручивается: иначе список
  // уезжает под пальцем, а решение остаётся непринятым.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <div className="sheet-backdrop" onClick={onClose} role="presentation">
      <div
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sheet-grip" />
        <strong style={{ fontSize: 16 }}>{title}</strong>
        {children}
        {confirmLabel !== undefined && (
          <button
            className={danger ? 'btn btn-danger' : 'btn'}
            disabled={busy || disabled}
            onClick={onConfirm}
          >
            {busy ? '…' : confirmLabel}
          </button>
        )}
        <button className="btn btn-ghost" disabled={busy} onClick={onClose}>
          {closeLabel ?? t('state.close')}
        </button>
      </div>
    </div>
  );
}
